// ISDP CUSTOM: the details screen's About / relations / links cards.
//
// They live together in one component because their grid SPANS depend on what the
// entity actually has, which only React can decide (the tab layouts are static JSX):
//  - the relations graph is dropped when the entity has nothing to graph, and
//  - About then widens to the whole row rather than leaving an empty grid column.
// The links card is dropped when the entity declares no links. Every kind's Details
// tab composes these instead of hardcoding md6/md6/md4 wrappers.
import { ReactNode } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityLinksCard } from '@backstage/plugin-catalog';
import { EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import { EntityAboutCard } from './EntityAboutCard';
import { hasGraphableRelations } from './entityHelpers';

/**
 * About + the relations graph, side by side — or About alone, full width, when the
 * entity has no relations worth graphing (see hasGraphableRelations).
 */
export const EntityAboutRow = () => {
  const { entity } = useEntity();
  const withGraph = hasGraphableRelations(entity);
  return (
    <>
      <div
        className={
          withGraph
            ? 'devhub-entity-grid__item--md6'
            : 'devhub-entity-grid__item--md12'
        }
      >
        <EntityAboutCard wide={!withGraph} />
      </div>
      {withGraph && (
        <div className="devhub-entity-grid__item--md6">
          <EntityCatalogGraphCard height={400} />
        </div>
      )}
    </>
  );
};

/** The entity's `metadata.links` card — nothing at all when there are no links. */
export const EntityLinksSection = () => {
  const { entity } = useEntity();
  if (!entity.metadata.links?.length) {
    return null;
  }
  return (
    <div className="devhub-entity-grid__item--md4">
      <EntityLinksCard />
    </div>
  );
};

/**
 * Renders a relation-driven Backstage card ONLY when the entity actually has a relation
 * of that shape, and nothing at all otherwise.
 *
 * Those cards render a full table — six column headers and a "No component provides this
 * API." row — when there is nothing to list. On this catalog that is the usual case, so an
 * API page carried two large empty tables saying nothing, which is the same complaint that
 * got the relations graph and the links card hidden. Backstage gives them no "hide when
 * empty" option, so the check happens here, on the entity's own relations (no fetch).
 *
 * `kind` is needed because every "Has …" card reads the SAME `hasPart` relation and merely
 * filters by target kind — a System with components but no resources must keep Components
 * and drop Resources.
 */
export const IfRelation = ({
  type,
  kind,
  className,
  children,
}: {
  type: string;
  kind?: string;
  className: string;
  children: ReactNode;
}) => {
  const { entity } = useEntity();
  const present = (entity.relations ?? []).some(
    r =>
      r.type === type &&
      (!kind || r.targetRef.toLowerCase().startsWith(`${kind.toLowerCase()}:`)),
  );
  if (!present) {
    return null;
  }
  return <div className={className}>{children}</div>;
};
